import { Controller } from '@hotwired/stimulus'
import Swiper from 'swiper'
import { Zoom } from 'swiper/modules'

type Slide = {
  src: string
  caption: string
  link: HTMLAnchorElement
}

type Drag = {
  pointerId: number
  startX: number
  startY: number
  dy: number
  active: boolean
  startedAt: number
  // 背景の減光量は表示中の画像の高さを基準にする
  fadeBase: number
}

const SLIDESHOW_DELAY = 3000
const IDLE_DELAY = 3500
const OPEN_CLOSE_DURATION = 250
// 下方向ドラッグで閉じる判定。この距離まで引けば閉じる
const DRAG_CLOSE_DISTANCE = 200
// 短い距離でも、この距離をこの時間未満で払えば閉じる（フリック）
const DRAG_FLICK_DISTANCE = 50
const DRAG_FLICK_DURATION = 300
// 矢印送り・矢印キー・スライドショーのクロスフェード速度。開閉ゴースト(OPEN_CLOSE_DURATION)とは別速度
const SLIDE_FADE_DURATION = 200
// transitionendが発火しない環境（transition無効化等）でも処理が進むよう、フォールバックタイマーに使う余裕込みの時間
const TRANSITION_FALLBACK = OPEN_CLOSE_DURATION + 150
const SLIDE_FADE_FALLBACK = SLIDE_FADE_DURATION + 150

export default class extends Controller {
  static targets = ['item']

  declare itemTargets: HTMLAnchorElement[]
  declare slides: Slide[]
  declare overlay: HTMLElement | undefined
  declare swiper: Swiper | undefined
  declare closing: boolean
  declare idleTimer: number | undefined
  declare drag: Drag | undefined
  declare slideshowTimer: number | undefined
  declare advanceGhost: HTMLImageElement | undefined
  declare advanceFadeImage: HTMLImageElement | undefined
  declare advanceFadeTimer: number | undefined
  declare previousActiveElement: HTMLElement | undefined

  open(event: Event) {
    const clicked = (event.target as HTMLElement).closest('a')
    if (!clicked || !this.itemTargets.includes(clicked)) return

    event.preventDefault()
    if (this.overlay) return

    const links = this.itemTargets
    this.slides = links.map((link) => ({ src: link.href, caption: link.dataset.lightboxCaption ?? '', link }))
    this.closing = false
    // 閉じたときにフォーカスを戻す先として、開く直前のフォーカス位置を控えておく
    this.previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : undefined
    this.overlay = this.render()
    document.body.append(this.overlay)
    this.initSwiper(this.overlay, links.indexOf(clicked))
    this.layoutSlides()

    document.body.classList.add('modal-open')
    document.addEventListener('keydown', this.onKeydown)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    this.resetIdle()

    this.overlay.getBoundingClientRect() // 強制reflowでappendとクラス付与を区切り、フェードを発火させる
    this.overlay.classList.add('lightbox--visible')
    // 背後ページのスクロール位置を動かさずにフォーカスをダイアログ内へ移す
    this.overlay.focus({ preventScroll: true })
    this.animateOpen(clicked)
  }

  close() {
    const overlay = this.overlay
    if (!overlay || this.closing) return

    this.closing = true
    // 破棄済み・開き直し後に遅れて発火した場合はoverlayの同一性チェックにより何もしない
    const finish = () => {
      if (this.overlay === overlay) this.teardown()
    }

    const ghost = this.createCloseGhost()
    if (ghost) {
      overlay.append(ghost.element)
      this.swiperEl()?.classList.add('lightbox__swiper--hidden')
      ghost.element.getBoundingClientRect()
      ghost.run()
    }
    this.finishOnFadeOut(overlay, finish)
    overlay.classList.remove('lightbox--visible')
  }

  // overlay自身のフェード完了で後始末する。子要素のtransitionendがバブリングしてきても早期に発火しない
  private finishOnFadeOut(overlay: HTMLElement, finish: () => void) {
    overlay.addEventListener('transitionend', (event) => {
      if (event.target === overlay) finish()
    })
    setTimeout(finish, TRANSITION_FALLBACK)
  }

  disconnect() {
    // 開いたままTurbo遷移した場合の残骸防止。アニメーションは待たず即時破棄する
    this.teardown()
  }

  private onKeydown = (event: KeyboardEvent) => {
    this.resetIdle()
    if (event.key === 'Escape') this.close()
    if (event.key === 'ArrowRight') this.advance('next')
    if (event.key === 'ArrowLeft') this.advance('prev')
    if (event.key === 'Tab') this.trapFocus(event)
  }

  // Tab/Shift+Tabがオーバーレイの外（背後のページ）へ抜けないよう、フォーカス可能要素の間で循環させる
  private trapFocus(event: KeyboardEvent) {
    const overlay = this.overlay
    if (!overlay) return

    // disabled切替やスライド送りで対象が変わるため、押下のたびに取り直す
    const focusable = this.focusableElements(overlay)
    const first = focusable[0] ?? overlay
    const last = focusable[focusable.length - 1] ?? overlay
    const active = document.activeElement

    if (event.shiftKey) {
      // オーバーレイ自身にフォーカスがある状態（開いた直後）からのShift+Tabは、既定動作だと背後のページへ抜けてしまう
      if (active === first || active === overlay || !overlay.contains(active)) {
        event.preventDefault()
        last.focus()
      }
    } else if (active === last || !overlay.contains(active)) {
      event.preventDefault()
      first.focus()
    }
  }

  private focusableElements(overlay: HTMLElement): HTMLElement[] {
    return Array.from(
      overlay.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'),
    )
  }

  private onFullscreenChange = () => {
    this.overlay?.classList.toggle('lightbox--fullscreen', Boolean(document.fullscreenElement))
  }

  private render(): HTMLElement {
    const multiple = this.slides.length > 1
    const overlay = document.createElement('div')
    overlay.className = 'lightbox'
    overlay.setAttribute('role', 'dialog')
    overlay.setAttribute('aria-modal', 'true')
    overlay.setAttribute('aria-label', 'You can close this modal content with the ESC key')
    overlay.tabIndex = -1
    overlay.innerHTML = `
      <div class="swiper lightbox__swiper">
        <div class="swiper-wrapper"></div>
        ${
  multiple
    ? `
        <button class="lightbox__button lightbox__button--prev" type="button" aria-label="Previous">
          <iconify-icon icon="mdi:chevron-left"></iconify-icon>
        </button>
        <button class="lightbox__button lightbox__button--next" type="button" aria-label="Next">
          <iconify-icon icon="mdi:chevron-right"></iconify-icon>
        </button>`
    : ''
}
      </div>
      <div class="lightbox__toolbar">
        ${multiple ? `<div class="lightbox__counter"><span class="lightbox__counter-current"></span>/<span>${this.slides.length}</span></div>` : '<div></div>'}
        <div class="lightbox__toolbar-buttons">
          <button class="lightbox__toolbar-button lightbox__zoom" type="button" aria-label="Toggle zoom level">
            <iconify-icon class="lightbox__icon-zoom-in" icon="mdi:magnify-plus-outline"></iconify-icon>
            <iconify-icon class="lightbox__icon-zoom-out" icon="mdi:magnify-minus-outline"></iconify-icon>
          </button>
          ${
  multiple
    ? `
          <button class="lightbox__toolbar-button lightbox__slideshow" type="button" aria-label="Toggle slideshow">
            <iconify-icon class="lightbox__icon-play" icon="mdi:play-outline"></iconify-icon>
            <iconify-icon class="lightbox__icon-pause" icon="mdi:pause"></iconify-icon>
          </button>`
    : ''
}
          ${
  document.fullscreenEnabled
    ? `
          <button class="lightbox__toolbar-button lightbox__fullscreen" type="button" aria-label="Toggle full-screen mode">
            <iconify-icon class="lightbox__icon-expand" icon="mdi:fullscreen"></iconify-icon>
            <iconify-icon class="lightbox__icon-compress" icon="mdi:fullscreen-exit"></iconify-icon>
          </button>`
    : ''
}
          <button class="lightbox__toolbar-button lightbox__close" type="button" aria-label="Close">
            <iconify-icon icon="mdi:close"></iconify-icon>
          </button>
        </div>
      </div>
    `

    const wrapper = overlay.querySelector('.swiper-wrapper') as HTMLElement
    wrapper.append(...this.slides.map((slide) => this.renderSlide(slide)))

    overlay.querySelector('.lightbox__close')?.addEventListener('click', () => this.close())
    overlay.querySelector('.lightbox__zoom')?.addEventListener('click', () => this.swiper?.zoom.toggle())
    overlay.querySelector('.lightbox__slideshow')?.addEventListener('click', () => this.toggleSlideshow())
    overlay.querySelector('.lightbox__fullscreen')?.addEventListener('click', () => this.toggleFullscreen())
    overlay.querySelector('.lightbox__button--prev')?.addEventListener('click', () => this.advance('prev'))
    overlay.querySelector('.lightbox__button--next')?.addEventListener('click', () => this.advance('next'))
    overlay.addEventListener('wheel', this.onWheel, { passive: false })
    // ポインタを乗せている間は自動送りを待たせる（外すと最初から数え直す）
    overlay.addEventListener('mouseenter', () => this.pauseSlideshow())
    overlay.addEventListener('mouseleave', () => this.resumeSlideshow())
    overlay.addEventListener('pointermove', this.resetIdle)
    overlay.addEventListener('pointerdown', this.onPointerDown)
    overlay.addEventListener('pointermove', this.onPointerMove)
    overlay.addEventListener('pointerup', this.onPointerUp)
    overlay.addEventListener('pointercancel', this.onPointerCancel)

    return overlay
  }

  private renderSlide(slide: Slide): HTMLElement {
    // URLはDOM API経由で代入する（テンプレートリテラル埋め込みによる属性インジェクション回避）
    const image = document.createElement('img')
    image.className = 'lightbox__image'
    image.src = slide.src
    // captionを優先し、無ければサムネイルのaltを引き継ぐ（拡大した途端に代替テキストが失われないようにする）
    image.alt = slide.caption || slide.link.querySelector('img')?.alt || ''
    image.loading = 'lazy'

    const zoomContainer = document.createElement('div')
    zoomContainer.className = 'swiper-zoom-container'
    zoomContainer.append(image)

    const slideEl = document.createElement('div')
    slideEl.className = 'swiper-slide'
    slideEl.append(zoomContainer)

    // ロード完了までスライドにスピナーを表示する
    if (image.complete) {
      slideEl.classList.add('lightbox__slide--loaded')
    } else {
      const finishLoading = () => {
        slideEl.classList.add('lightbox__slide--loaded')
        this.updateZoomButton()
      }
      image.addEventListener('load', finishLoading, { once: true })
      image.addEventListener('error', finishLoading, { once: true })
    }

    if (slide.caption) {
      const caption = document.createElement('div')
      caption.className = 'lightbox__caption'
      caption.textContent = slide.caption
      slideEl.append(caption)
    }

    return slideEl
  }

  private initSwiper(overlay: HTMLElement, initialSlide: number) {
    const multiple = this.slides.length > 1
    this.swiper = new Swiper(overlay.querySelector('.lightbox__swiper') as HTMLElement, {
      modules: [Zoom],
      initialSlide,
      loop: multiple,
      // loading="lazy"の隣接スライド画像を先読みさせる
      lazyPreloadPrevNext: 1,
      // ドラッグ解放時のスナップアニメーションに使う（送り自体はadvance()内でspeed:0を指定し、自前のクロスフェードに任せる）
      speed: 200,
      spaceBetween: 10, // スライド間の余白
      // 最大ズーム=原寸。ダブルタップズームは無効化し、クリック/タップでのトグルに統一する
      zoom: { limitToOriginalSize: true, maxRatio: 100, toggle: false },
      on: {
        slideChange: () => this.onSlideChange(),
        // 画面サイズ変更時の再レイアウトはwindowのresizeではなくここで行う。
        // windowのresize時点ではSwiperがまだスライド幅を更新しておらず、キャプションを古い幅で測ってしまい
        // 差し引く高さを小さく見積もって画像が過大に残る
        resize: () => {
          this.layoutSlides()
          this.updateZoomButton()
        },
        zoomChange: (_swiper, scale) => {
          this.overlay?.classList.toggle('lightbox--zoomed', scale > 1)
          // ズームするとスライドショーは停止する（ボタン・画像クリック・ホイールいずれの操作でも）
          if (scale > 1) this.stopSlideshow()
          // SPのピンチズームはSwiperのZoomモジュール内部で処理されるためonWheelを経由しない。ここで拾ってアイドル状態を解除する
          this.resetIdle()
        },
        // Swiperのclickイベントはドラッグ（スワイプ）後には発火しないため、誤クローズしない
        click: (_swiper, clickEvent) => this.onSwiperClick(clickEvent),
        // SPのスワイプ送りもSwiper内部でイベント伝播が止まりpointermoveがoverlayまで届かない。
        // touchStartはユーザー操作時のみ発火する（プログラム的なslideNext呼び出しでは発火しない）ためここでアイドル解除する
        touchStart: () => this.resetIdle(),
      },
    })
    this.updateCounter()
    this.updateZoomButton()
  }

  // 画像+キャプションのグループごと上下中央に置くため、キャプション分を差し引いた高さを画像の上限にする
  private layoutSlides() {
    const overlay = this.overlay
    if (!overlay) return

    const available = overlay.clientHeight - 8 // スライドの上下padding分
    for (const slideEl of overlay.querySelectorAll<HTMLElement>('.swiper-slide')) {
      const caption = slideEl.querySelector<HTMLElement>('.lightbox__caption')
      const image = slideEl.querySelector<HTMLElement>('.lightbox__image')
      if (image) image.style.maxHeight = `${available - (caption?.offsetHeight ?? 0)}px`
    }
  }

  private onSlideChange() {
    this.updateCounter()
    this.updateZoomButton()
    if (this.overlay?.classList.contains('lightbox--playing')) this.restartProgress()
  }

  private onSwiperClick(event: MouseEvent | TouchEvent | PointerEvent) {
    const target = event.target as HTMLElement
    if (target.closest('.lightbox__button, .lightbox__toolbar, .lightbox__caption')) return

    if (target.closest('.lightbox__image')) {
      // 画像クリックはクリック位置に向かってfit⇔原寸のズームトグル
      if (this.canZoom()) this.swiper?.zoom.toggle(event)
      return
    }

    this.close()
  }

  private onWheel = (event: WheelEvent) => {
    event.preventDefault() // ライトボックス表示中のページズーム・スクロールを抑止
    this.resetIdle()
    if (!this.swiper || !this.canZoom()) return

    // ズームはfit⇔原寸の2段階なので、ホイールもトグルで表現する
    const zoomed = this.swiper.zoom.scale > 1
    if (event.deltaY < 0 ? !zoomed : zoomed) this.swiper.zoom.toggle(event)
  }

  // slideChangeハンドラ内で呼ばれた時点では.swiper-slide-activeクラスの付け替えが間に合っておらず、
  // 1つ前のスライドを指してしまうため、Swiper自身が持つactiveIndexで参照する
  private activeImage(): HTMLImageElement | null {
    const slide = this.swiper?.slides[this.swiper.activeIndex]
    return slide?.querySelector('.lightbox__image') ?? null
  }

  // 最大ズームは原寸。fit表示の時点で原寸なら、それ以上拡大できない
  private canZoom(): boolean {
    const image = this.activeImage()
    return Boolean(image && image.naturalWidth > image.clientWidth + 1)
  }

  private updateZoomButton() {
    const button = this.overlay?.querySelector<HTMLButtonElement>('.lightbox__zoom')
    if (button) button.disabled = !this.canZoom()
  }

  private updateCounter() {
    if (!this.overlay || !this.swiper) return

    const current = this.overlay.querySelector('.lightbox__counter-current')
    if (current) current.textContent = String(this.swiper.realIndex + 1)
  }

  private toggleSlideshow() {
    if (!this.overlay || !this.swiper) return

    if (this.overlay.classList.contains('lightbox--playing')) {
      this.stopSlideshow()
    } else {
      this.overlay.classList.add('lightbox--playing')
      this.startSlideshowTimer()
    }
  }

  // Navigation/Keyboardモジュールと同様、Autoplayモジュールも内部でparams.speedを使い送ってしまい
  // クロスフェードを挟めないため、自前タイマーでadvance()を呼ぶ
  private startSlideshowTimer() {
    this.slideshowTimer = window.setInterval(() => this.advance('next'), SLIDESHOW_DELAY)
    this.restartProgress()
  }

  private stopSlideshow() {
    if (!this.overlay || !this.swiper) return

    this.overlay.classList.remove('lightbox--playing')
    this.pauseSlideshow()
  }

  // 再生状態は保ったまま自動送りだけ待たせる。プログレスバーは消し、再開時に0から数え直す
  private pauseSlideshow() {
    if (!this.slideshowTimer) return

    window.clearInterval(this.slideshowTimer)
    this.slideshowTimer = undefined
    this.overlay?.querySelector('.lightbox__progress')?.remove()
  }

  private resumeSlideshow() {
    if (!this.overlay?.classList.contains('lightbox--playing') || this.slideshowTimer) return
    // ドラッグで閉じかけている途中は再開しない
    if (this.drag) return

    this.startSlideshowTimer()
  }

  private restartProgress() {
    const button = this.overlay?.querySelector('.lightbox__slideshow')
    if (!button) return

    let progress = button.querySelector<HTMLElement>('.lightbox__progress')
    if (!progress) {
      progress = document.createElement('div')
      progress.className = 'lightbox__progress'
      button.prepend(progress)
    }
    progress.style.transition = 'none'
    progress.style.transform = 'scaleX(0)'
    progress.getBoundingClientRect() // 強制reflowでリセットと再スタートを区切る
    progress.style.transition = `transform ${SLIDESHOW_DELAY}ms linear`
    progress.style.transform = 'scaleX(1)'
  }

  private toggleFullscreen() {
    if (document.fullscreenElement) {
      void document.exitFullscreen()
    } else {
      // lightbox要素単体ではなくページ全体をフルスクリーン化する（背後のページが透けて見える見た目を再現するため）
      document.documentElement.requestFullscreen().catch(() => {}) // 拒否されたら何もしない（アイコンはfullscreenchange経由でのみ切り替わる）
    }
  }

  // idle挙動: 3.5秒無操作でツールバーと矢印をフェードアウトし、ポインタ操作で復帰する
  private resetIdle = () => {
    const overlay = this.overlay
    if (!overlay) return

    overlay.classList.remove('lightbox--idle')
    window.clearTimeout(this.idleTimer)
    this.idleTimer = window.setTimeout(() => overlay.classList.add('lightbox--idle'), IDLE_DELAY)
  }

  private swiperEl(): HTMLElement | null {
    return this.overlay?.querySelector('.lightbox__swiper') ?? null
  }

  // クリックされたリンク内の「実際に見えている」サムネイル画像。不可視ならズーム遷移せずフォールバック演出になる
  private visibleThumb(link: HTMLAnchorElement | undefined): HTMLImageElement | null {
    const thumb = link?.querySelector('img')
    if (!thumb) return null

    const rect = thumb.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0 ? thumb : null
  }

  private animateOpen(clicked: HTMLAnchorElement) {
    const swiperEl = this.swiperEl()
    if (!swiperEl) return

    const thumb = this.visibleThumb(clicked)
    const image = this.activeImage()
    if (!thumb || !image) {
      swiperEl.classList.add('lightbox__swiper--zoom-in-up')
      return
    }

    // 拡大画像のロード完了後の実寸レイアウトを遷移先にする
    swiperEl.classList.add('lightbox__swiper--hidden')
    const reveal = () => swiperEl.classList.remove('lightbox__swiper--hidden')
    // ロードが長引く場合はズーム演出を諦めて表示する（スライド内のスピナーに委ねる）
    const revealTimer = setTimeout(reveal, 4000)
    const start = () => {
      clearTimeout(revealTimer)
      if (this.overlay !== swiperEl.closest('.lightbox') || this.closing) return
      if (!swiperEl.classList.contains('lightbox__swiper--hidden')) return

      const to = image.getBoundingClientRect()
      if (!to.width) {
        reveal()
        return
      }

      const ghost = this.buildGhost(image.currentSrc || image.src, thumb.getBoundingClientRect(), to)
      this.overlay?.append(ghost)
      ghost.getBoundingClientRect() // 強制reflowで開始位置を確定させ、遷移を発火させる
      ghost.style.transform = 'translate(0, 0) scale(1, 1)'
      ghost.style.opacity = '1'
      const finish = () => {
        reveal()
        ghost.remove()
      }
      ghost.addEventListener('transitionend', finish, { once: true })
      setTimeout(finish, TRANSITION_FALLBACK)
    }

    if (image.complete && image.naturalWidth) {
      start()
    } else {
      image.addEventListener('load', start, { once: true })
      image.addEventListener('error', () => swiperEl.classList.remove('lightbox__swiper--hidden'), { once: true })
    }
  }

  private createCloseGhost(): { element: HTMLElement; run: () => void } | null {
    const image = this.activeImage()
    const slide = this.slides[this.swiper?.realIndex ?? 0]
    const thumb = this.visibleThumb(slide?.link)
    if (!image || !thumb || !image.complete || !image.naturalWidth) return null

    const from = image.getBoundingClientRect()
    const to = thumb.getBoundingClientRect()
    if (!from.width || !to.width) return null

    const ghost = this.buildGhost(image.currentSrc || image.src, from, from)
    const run = () => {
      ghost.style.transform = `translate(${to.x - from.x}px, ${to.y - from.y}px) scale(${to.width / from.width}, ${to.height / from.height})`
      // サムネイルがCSSでトリミングされている等、行き先とアスペクト比が異なる場合はフェードで馴染ませる
      if (this.aspectDiffers(from, to)) ghost.style.opacity = '0'
      setTimeout(() => ghost.remove(), TRANSITION_FALLBACK)
    }
    return { element: ghost, run }
  }

  // fromの位置・サイズに置き、toから逆算したtransformで遷移させるゴースト画像（FLIP方式）
  private buildGhost(src: string, from: DOMRect, to: DOMRect): HTMLImageElement {
    const ghost = document.createElement('img')
    ghost.src = src
    ghost.className = 'lightbox__ghost'
    ghost.style.left = `${to.x}px`
    ghost.style.top = `${to.y}px`
    ghost.style.width = `${to.width}px`
    ghost.style.height = `${to.height}px`
    ghost.style.transform = `translate(${from.x - to.x}px, ${from.y - to.y}px) scale(${from.width / to.width}, ${from.height / to.height})`
    if (this.aspectDiffers(from, to)) ghost.style.opacity = '0.3'
    return ghost
  }

  private aspectDiffers(a: DOMRect, b: DOMRect): boolean {
    return Math.abs(a.width / a.height - b.width / b.height) > 0.02
  }

  // 矢印ボタン・矢印キー・スライドショーの送り口を集約するメソッド。ドラッグ／スワイプ中の位置追従はSwiper標準に任せ、ここは通らない
  private advance(direction: 'next' | 'prev') {
    const overlay = this.overlay
    const swiper = this.swiper
    if (!overlay || !swiper || this.closing || this.slides.length <= 1) return

    this.cancelAdvanceFade() // 連打・キーリピート・スライドショーの重なりに備え、前回の演出を打ち切って次へ進む

    const ghost = this.buildAdvanceGhost(this.activeImage())
    // speed:0で位置を瞬時に切り替え、視覚的な送り演出はゴーストのクロスフェードに任せる
    if (direction === 'next') {
      swiper.slideNext(0)
    } else {
      swiper.slidePrev(0)
    }

    if (ghost) this.startAdvanceFade(overlay, ghost)
  }

  // 送り前のアクティブ画像をその場に静止するゴーストとして複製する。矩形が取れなければ演出なしで送る
  private buildAdvanceGhost(image: HTMLImageElement | null): HTMLImageElement | null {
    if (!image || !image.complete || !image.naturalWidth) return null

    const rect = image.getBoundingClientRect()
    if (!rect.width) return null

    const ghost = this.buildGhost(image.currentSrc || image.src, rect, rect)
    // 開閉ゴースト(OPEN_CLOSE_DURATION)とは別の送り用速度を指定する
    ghost.style.transition = `opacity ${SLIDE_FADE_DURATION}ms ease`
    return ghost
  }

  // ゴーストを1→0、新しいアクティブ画像を0→1で同時にフェードさせるクロスフェード演出
  private startAdvanceFade(overlay: HTMLElement, ghost: HTMLImageElement) {
    overlay.append(ghost)

    const newImage = this.activeImage()
    if (newImage) {
      newImage.style.transition = 'none'
      newImage.style.opacity = '0'
    }
    ghost.getBoundingClientRect() // 強制reflowで新画像opacity:0の初期状態を確定させてからフェードを開始する

    this.advanceGhost = ghost
    this.advanceFadeImage = newImage ?? undefined
    const finish = () => this.finishAdvanceFade(ghost, newImage)
    ghost.addEventListener('transitionend', finish, { once: true })
    this.advanceFadeTimer = window.setTimeout(finish, SLIDE_FADE_FALLBACK) // transitionend不発時のフォールバック

    if (newImage) {
      newImage.style.transition = `opacity ${SLIDE_FADE_DURATION}ms ease`
      newImage.style.opacity = '1'
    }
    ghost.style.opacity = '0'
  }

  private finishAdvanceFade(ghost: HTMLImageElement, newImage: HTMLImageElement | null) {
    // transitionendとフォールバックタイマーの両方から呼ばれるため、2回目以降（完了済み・打ち切り済み）は何もしない
    if (this.advanceGhost !== ghost) return

    window.clearTimeout(this.advanceFadeTimer)
    ghost.remove()
    if (newImage) {
      newImage.style.transition = ''
      newImage.style.opacity = ''
    }
    this.advanceGhost = undefined
    this.advanceFadeImage = undefined
    this.advanceFadeTimer = undefined
  }

  // 前回の送り演出が完了する前に次の送りが来た場合、ゴースト・フェード中画像・タイマーを破棄して多重演出を防ぐ
  private cancelAdvanceFade() {
    window.clearTimeout(this.advanceFadeTimer)
    this.advanceFadeTimer = undefined
    this.advanceGhost?.remove()
    this.advanceGhost = undefined
    if (this.advanceFadeImage) {
      this.advanceFadeImage.style.transition = ''
      this.advanceFadeImage.style.opacity = ''
      this.advanceFadeImage = undefined
    }
  }

  // dragToClose: ズームしていない状態での縦優勢ドラッグに追従し、閾値を超えたら閉じる
  private onPointerDown = (event: PointerEvent) => {
    if (this.drag || this.closing) return
    if (!this.swiper || this.swiper.zoom.scale > 1) return

    const target = event.target as HTMLElement
    if (target.closest('.lightbox__button, .lightbox__toolbar, .lightbox__caption')) return

    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      dy: 0,
      active: false,
      startedAt: performance.now(),
      fadeBase: this.activeImage()?.getBoundingClientRect().height || this.overlay?.clientHeight || 1,
    }
  }

  private onPointerMove = (event: PointerEvent) => {
    const drag = this.drag
    if (!drag || event.pointerId !== drag.pointerId) return

    // ピンチズームが始まったら追従をやめる
    if (this.swiper && this.swiper.zoom.scale > 1) {
      this.cancelDrag()
      return
    }

    const dx = event.clientX - drag.startX
    const dy = event.clientY - drag.startY
    if (!drag.active) {
      if (Math.abs(dx) >= Math.abs(dy) && Math.abs(dx) > 6) {
        // 横優勢はSwiperのスワイプ送りに任せる
        this.drag = undefined
        return
      }
      if (Math.abs(dy) <= 6) return
      drag.active = true
      this.stopSlideshow() // 閉じる操作中に写真が勝手に切り替わらないようにする
    }

    drag.dy = dy
    const swiperEl = this.swiperEl()
    if (swiperEl) {
      swiperEl.style.transition = 'none'
      swiperEl.style.transform = `translateY(${dy}px)`
    }
    if (this.overlay) {
      // 画像の高さを基準に減光する。半分(0.5)で下限を打ち、それ以上は暗くしない
      const fade = Math.max(0.5, Math.min(1, 1 - Math.abs(dy) / drag.fadeBase * 1.5))
      this.overlay.style.backgroundColor = `rgb(0 0 0 / ${0.85 * fade})`
    }
  }

  private onPointerUp = (event: PointerEvent) => {
    const drag = this.drag
    if (!drag || event.pointerId !== drag.pointerId) return

    this.drag = undefined
    if (!drag.active) return

    // 十分な距離を引いた場合と、短い距離でも素早く払った場合の2通りで閉じる
    const distance = Math.abs(drag.dy)
    const elapsed = performance.now() - drag.startedAt
    if (distance >= DRAG_CLOSE_DISTANCE || distance >= DRAG_FLICK_DISTANCE && elapsed < DRAG_FLICK_DURATION) {
      this.closeByDrag(drag.dy)
    } else {
      this.cancelDrag()
    }
  }

  private onPointerCancel = (event: PointerEvent) => {
    if (this.drag?.pointerId !== event.pointerId) return

    this.drag = undefined
    this.cancelDrag()
  }

  private cancelDrag() {
    this.drag = undefined
    const swiperEl = this.swiperEl()
    if (swiperEl) {
      swiperEl.style.transition = 'transform 0.2s ease'
      swiperEl.style.transform = ''
    }
    this.overlay?.style.removeProperty('background-color')
  }

  private closeByDrag(dy: number) {
    const overlay = this.overlay
    if (!overlay || this.closing) return

    this.closing = true
    const swiperEl = this.swiperEl()
    if (swiperEl) {
      swiperEl.style.transition = `transform ${OPEN_CLOSE_DURATION}ms ease-out`
      swiperEl.style.transform = `translateY(${Math.sign(dy) * overlay.clientHeight * 0.5}px)`
    }
    const finish = () => {
      if (this.overlay === overlay) this.teardown()
    }
    this.finishOnFadeOut(overlay, finish)
    overlay.style.removeProperty('background-color')
    overlay.classList.remove('lightbox--visible')
  }

  private teardown() {
    if (!this.overlay) return

    // 全画面化の対象はoverlayではなくページ全体なので、要素の同一性では判定できない
    if (document.fullscreenElement) void document.exitFullscreen()
    window.clearTimeout(this.idleTimer)
    window.clearInterval(this.slideshowTimer)
    this.slideshowTimer = undefined
    this.cancelAdvanceFade()
    this.drag = undefined
    this.swiper?.destroy(true, true)
    this.swiper = undefined
    document.removeEventListener('keydown', this.onKeydown)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    this.overlay.remove()
    this.overlay = undefined
    document.body.classList.remove('modal-open')

    // Turbo遷移によるdisconnect()経由のteardownでは、復帰先が既にDOMから外れている場合があるため確認してから戻す
    if (this.previousActiveElement?.isConnected) this.previousActiveElement.focus()
    this.previousActiveElement = undefined
  }
}
